import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import { categories, getArticlesByCategory } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentSlug = slug || 'feature-intro';

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentSlug]);

  const currentCategory = categories.find((c) => c.slug === currentSlug) || categories[0];
  const articles = getArticlesByCategory(currentSlug);

  const colorMap: Record<string, { active: string; bg: string; text: string; border: string }> = {
    primary: {
      active: 'oklch(var(--primary-500))',
      bg: 'oklch(var(--primary-100))',
      text: 'oklch(var(--primary-700))',
      border: 'oklch(var(--primary-300))',
    },
    accent: {
      active: 'oklch(var(--accent-500))',
      bg: 'oklch(var(--accent-100))',
      text: 'oklch(var(--accent-700))',
      border: 'oklch(var(--accent-300))',
    },
    secondary: {
      active: 'oklch(var(--secondary-500))',
      bg: 'oklch(var(--secondary-100))',
      text: 'oklch(var(--secondary-700))',
      border: 'oklch(var(--secondary-300))',
    },
  };

  const catColor = colorMap[currentCategory.color] || colorMap.primary;

  return (
    <main className="pt-20 pb-16" style={{ backgroundColor: 'oklch(var(--background-50))' }}>
      {/* Breadcrumb */}
      <div className="max-w-6xl mx-auto px-4 md:px-6 mb-8">
        <nav className="flex items-center gap-2 text-sm" aria-label="面包屑导航">
          <Link to="/" className="hover:underline cursor-pointer" style={{ color: 'oklch(var(--foreground-500))' }}>首页</Link>
          <span style={{ color: 'oklch(var(--foreground-400))' }}>/</span>
          <span className="font-medium" style={{ color: 'oklch(var(--foreground-950))' }}>{currentCategory.name}</span>
        </nav>
      </div>

      {/* Category Header */}
      <section className="w-full mb-10">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div
            className="rounded-lg p-8 md:p-12 text-center"
            style={{ backgroundColor: catColor.bg }}
          >
            <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center mx-auto mb-4 rounded-full" style={{ backgroundColor: catColor.active }}>
              <i className={`${currentCategory.icon} text-2xl md:text-3xl`} style={{ color: '#fff' }}></i>
            </div>
            <h1 className="text-2xl md:text-3xl font-heading mb-3" style={{ color: 'oklch(var(--foreground-950))' }}>
              {currentCategory.name}
            </h1>
            <p className="text-sm md:text-base max-w-lg mx-auto" style={{ color: 'oklch(var(--foreground-600))' }}>
              {currentCategory.description}
            </p>
          </div>
        </div>
      </section>

      {/* Category Tabs */}
      <section className="w-full mb-10 sticky top-16 z-30 py-3" style={{ backgroundColor: 'oklch(var(--background-50))' }}>
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className="px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all duration-200"
                style={
                  currentSlug === cat.slug
                    ? { backgroundColor: catColor.active, color: '#fff' }
                    : {
                        backgroundColor: 'oklch(var(--background-100))',
                        color: 'oklch(var(--foreground-600))',
                      }
                }
                onMouseEnter={(e) => {
                  if (currentSlug !== cat.slug) {
                    e.currentTarget.style.backgroundColor = 'oklch(var(--background-200))';
                  }
                }}
                onMouseLeave={(e) => {
                  if (currentSlug !== cat.slug) {
                    e.currentTarget.style.backgroundColor = 'oklch(var(--background-100))';
                  }
                }}
              >
                {cat.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="w-full">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          {articles.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <i className="ri-inbox-line text-3xl" style={{ color: 'oklch(var(--foreground-300))' }}></i>
              </div>
              <p className="text-sm" style={{ color: 'oklch(var(--foreground-500))' }}>该分类暂无文章</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {articles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="rounded-lg overflow-hidden cursor-pointer transition-all duration-200 group"
                  style={{ backgroundColor: 'oklch(var(--background-100))' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <div className="h-48 overflow-hidden">
                    <img
                      src={article.cardImage || ''}
                      alt={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      width={800}
                      height={500}
                    />
                  </div>
                  <div className="p-5">
                    <h3 className="text-base font-bold mb-2 line-clamp-2" style={{ color: 'oklch(var(--foreground-950))' }}>
                      {article.title}
                    </h3>
                    <p className="text-sm mb-4 line-clamp-2 leading-relaxed" style={{ color: 'oklch(var(--foreground-600))' }}>
                      {article.summary}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs" style={{ color: 'oklch(var(--foreground-400))' }}>{article.publishDate}</span>
                      <span className="flex items-center gap-1 text-xs" style={{ color: 'oklch(var(--foreground-400))' }}>
                        <div className="w-3 h-3 flex items-center justify-center">
                          <i className="ri-eye-line text-xs"></i>
                        </div>
                        {article.readCount.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Schema.org */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${currentCategory.name} - 雏鸟app`,
            description: currentCategory.description,
            url: `https://chuniao.app/category/${currentSlug}`,
            isPartOf: {
              '@type': 'WebSite',
              name: '雏鸟app官方介绍与下载指南',
              url: 'https://chuniao.app',
            },
          }),
        }}
      />
    </main>
  );
}